import { createFileRoute } from "@tanstack/react-router";
import { TrustBand } from "@/components/site/TrustBand";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { ValuesGrid } from "@/components/site/wow/ValuesGrid";
import { OriginScroll } from "@/components/site/wow/OriginScroll";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { TeamStrip } from "@/components/site/TeamStrip";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { Reveal } from "@/components/site/wow/Reveal";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { aeoGeo, breadcrumbs, localBusinessForCity } from "@/lib/seo/schema";
import { COMPANY_FACTS } from "@/content/company-facts";

export const Route = createFileRoute("/om-fenny")({
  head: () => ({
    meta: [
      { title: "Om Fenny — Premiumfilm som abonnemang" },
      {
        name: "description",
        content:
          "Lär känna Fenny: svensk filmproduktion med Pulse för löpande film och Prime för skräddarsydda projekt. Se teamet och hur vi arbetar.",
      },
      { property: "og:title", content: "Om Fenny" },
      {
        property: "og:description",
        content:
          "Smartare alternativ till traditionell filmproduktion. Premiumfilm som abonnemang för marknadsteam.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://fenny.se/om-fenny" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/om-fenny" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/om-fenny", name: "Om Fenny — Premiumfilm som abonnemang", description: "Fenny är ett smartare alternativ till traditionell filmproduktion: rörligt innehåll på abonnemang för marknadsteam som vill ha löpande effekt."})) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Om Fenny", path: "/om-fenny" }])) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessForCity({ city: "Stockholm", path: "/om-fenny", postalRegion: "Stockholms län", description: "Fenny är en svensk filmbyrå med bas i Stockholm. Vi levererar premiumfilm som abonnemang (Pulse), skräddarsydda produktioner (Prime). Första klippet inom 10–14 dagar." })) },
    ],
  }),
  component: OmFennyPage,
});

function OmFennyPage() {
  return (
    <div>
      {/* Manifesto hero */}
      <section className="relative overflow-hidden pt-32 pb-12 md:pt-44 md:pb-20">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_80%_10%,color-mix(in_oklab,var(--butter)_12%,transparent),transparent_70%)]"
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="mb-6 text-xs uppercase tracking-[0.22em] text-butter">Om Fenny</div>
          <KineticHeading
            text="Vi gör film för människor — inte för algoritmen."
            className="max-w-[20ch] text-balance text-5xl font-semibold leading-[0.98] text-foreground md:text-7xl"
            highlight={{ indices: [3, 4], className: "text-mint italic font-serif font-normal" }}
          />
          <p className="mt-10 max-w-2xl text-pretty text-lg text-muted-foreground md:text-xl">
            Fenny har funnits sedan {COMPANY_FACTS.foundedYear} och växte fram som svar på en sak vi tröttnat på: filmproduktion
            som tar för lång tid, kostar oförutsägbart och levererar för sällan.
            Vi gör det omvänt — abonnemang, fast pris, rytm.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Magnetic strength={0.3}>
              <Link
                to="/kontakt"
                className="group inline-flex items-center gap-3 rounded-full bg-mint px-8 py-4 text-base font-semibold text-mint-foreground hover:shadow-[0_20px_60px_-12px] hover:shadow-mint/50"
              >
                Boka strategimöte
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Magnetic>
            <Link
              to="/tjanster"
              className="inline-flex items-center gap-2 text-sm text-foreground/80 underline-offset-4 hover:text-butter hover:underline"
            >
              Se våra tjänster →
            </Link>
            <Link
              to="/fenny-i-korthet"
              className="inline-flex items-center gap-2 text-sm text-foreground/80 underline-offset-4 hover:text-butter hover:underline"
            >
              Fenny i korthet — fakta →
            </Link>
          </div>
        </div>
      </section>

      <Reveal>
        <ValuesGrid />
      </Reveal>

      <Reveal>
        <OriginScroll />
      </Reveal>

      <Reveal>
        <TeamStrip />
      </Reveal>

      <TrustBand
        eyebrow="Norrhavet Group"
        title="Fenny är film. Group är hela kedjan."
        body="Vi sitter under samma tak som Norrhavet och Ellary. Behöver ni branding, en landningssida som matchar kampanjen, eller annonsering som skalar — det är samma team, samma projektledare, samma faktura."
        ctaLocation="om_fenny_trustband"
      />

      <Reveal>
        <section className="py-24 md:py-32">
          <div className="container-wide">
            <div className="grid gap-12 md:grid-cols-2 md:items-start">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-butter">Tonalitet</div>
                <h2 className="mt-4 text-balance text-3xl font-semibold leading-tight md:text-5xl">
                  Rakt. Smart. Människonära.
                </h2>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
                  Vi pratar med er som med vänner som råkar vara bra på film. Inga
                  buzzwords, inga säljiga floskler. Vi förklarar vad vi gör, varför
                  vi gör det och vad det kommer att kosta — innan vi börjar.
                </p>
              </div>
              <blockquote className="relative rounded-3xl border border-white/10 bg-surface p-8 md:p-10">
                <div
                  aria-hidden
                  className="absolute -top-6 -left-2 text-7xl font-serif text-mint/30"
                >
                  "
                </div>
                <p className="relative text-pretty text-xl font-medium leading-snug text-foreground md:text-2xl">
                  Jag har sällan varit med om något liknande. Fenny tar ansvar,
                  tänker längre och levererar med sådan finess att det känns nästan
                  för bra för att vara sant.
                </p>
                <footer className="mt-6 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Maria Borg</span>
                  {" · "}Garageportexperten
                </footer>
              </blockquote>
            </div>
          </div>
        </section>
      </Reveal>

      <SignatureCta tone="peach" />
    </div>
  );
}
